<script setup lang="ts">
import { Dialog } from "../../../../base-components/Headless";
import {
  FormLabel,
  FormInput,
  FormSwitch
} from "../../../../base-components/Form";
import FormSelect from "../../../../base-components/Form/FormSelect.vue";
import Button from "../../../../base-components/Button";
import LoadingIcon from "../../../../base-components/LoadingIcon";
import { ref, computed, onBeforeMount, watch } from "vue";
import { useMesasStore, PRESET_TAMANOS_MESA } from "../../../../services/mesas";
import { useSectoresStore } from "../../../../services/sectores";
import { useMensajesStore } from "../../../../services/mensajes";
import type { LogAcciones, Mesa } from "@fixeduy/restaurantestypes";
import { useLogAccionesStore } from "../../../../services/logAcciones";
import { getCurrentTime } from "../../../../utils/fecha";
import { userUserStore } from "../../../../stores/user";

interface Props {
  mesa: Mesa;
}

const logAccionesStore = useLogAccionesStore();

const props = defineProps<Props>();
const emit = defineEmits<{ (e: "cerrar"): void }>();

const mesasStore = useMesasStore();
const sectoresStore = useSectoresStore();
const mensajesStore = useMensajesStore();
const userStore = userUserStore();

onBeforeMount(() => {
  if (sectoresStore.sectores.length === 0) sectoresStore.getFromFirestore();
});

const nombre = ref(props.mesa.nombre);
const activa = ref(props.mesa.activo ?? true);
const sectorId = ref<string>(props.mesa.sectorId ?? "");
const generalSectorId = computed(() => {
  const sectores = sectoresStore.sectores ?? [];
  return (
    sectores.find(s => (s.nombre ?? "").trim().toLowerCase() === "general")
      ?.id ?? ""
  );
});
const cargando = ref(false);
const errorNombreDuplicado = ref<string>("");

watch(
  () => generalSectorId.value,
  id => {
    if (!sectorId.value && id) sectorId.value = id;
  },
  { immediate: true }
);
/** Preset de tamaño en mapa (personas). '' = no cambiar / personalizado. */
const presetPersonas = ref<number | "">("");

const estaLibre = computed(() => {
  const e = (props.mesa.estado || "").toLowerCase();
  return e === "disponible";
});

const guardar = async () => {
  errorNombreDuplicado.value = "";
  const nombreTrim = nombre.value.trim();
  if (!nombreTrim) {
    mensajesStore.mostrarModal(
      "Nombre requerido",
      "Ingresá el nombre de la mesa.",
      mensajesStore.mensajeType.error
    );
    return;
  }
  const nombreCambiado = nombreTrim !== props.mesa.nombre;
  const activoCambiado =
    estaLibre.value && activa.value !== (props.mesa.activo ?? true);
  const sectorCambiado = sectorId.value !== (props.mesa.sectorId ?? "");
  const presetCambiado = presetPersonas.value !== "";
  if (
    !nombreCambiado &&
    !activoCambiado &&
    !sectorCambiado &&
    !presetCambiado
  ) {
    emit("cerrar");
    return;
  }
  cargando.value = true;
  try {
    if (presetPersonas.value !== "" && props.mesa.ubicacion) {
      const preset = PRESET_TAMANOS_MESA.find(
        p => p.personas === Number(presetPersonas.value)
      );
      if (preset) {
        await mesasStore.updateUbicacion(props.mesa.id, {
          ...props.mesa.ubicacion,
          ancho: preset.ancho,
          alto: preset.alto
        });
      }
    }
    const datos: {
      nombre: string;
      activo?: boolean;
      sectorId?: string | null;
    } = { nombre: nombreTrim };
    if (estaLibre.value) datos.activo = activa.value;
    const resolvedSectorId = sectorId.value || generalSectorId.value;
    datos.sectorId = resolvedSectorId || null;
    const ok = await mesasStore.updateMesa(props.mesa.id, datos);
    if (ok === true) {
      const logAcciones: LogAcciones = {
        id: "",
        accion: [`Se modifica la mesa ${props.mesa.nombre} (${props.mesa.id})`],
        modulo: "mesas",
        fecha: getCurrentTime(),
        usuario: userStore.userLoggedIn?.uid!,
        mail: userStore.userLoggedIn?.email!
      };

      await logAccionesStore.guardarLog(logAcciones);

      const detalle =
        estaLibre.value && !activa.value
          ? `Se desactivó la mesa "${nombreTrim}".`
          : `Se actualizó la mesa "${nombreTrim}".`;
      mensajesStore.mostrarToast(
        "Mesa actualizada",
        detalle,
        mensajesStore.mensajeType.exito
      );
      emit("cerrar");
    } else {
      if (typeof ok === "string") {
        errorNombreDuplicado.value = ok;
      } else {
        mensajesStore.mostrarModal(
          "Error",
          "No se pudo actualizar la mesa. Intentá de nuevo.",
          mensajesStore.mensajeType.error
        );
      }
    }
  } finally {
    cargando.value = false;
  }
};

watch(nombre, () => {
  if (errorNombreDuplicado.value) errorNombreDuplicado.value = "";
});
</script>

<template>
  <Dialog :open="true" @close="() => emit('cerrar')" size="md">
    <Dialog.Panel>
      <Dialog.Title>
        <h2 class="mr-auto text-base font-medium">Editar mesa</h2>
      </Dialog.Title>

      <Dialog.Description>
        <div class="mt-3">
          <FormLabel htmlFor="mesa-editar-nombre">Nombre</FormLabel>
          <FormInput
            id="mesa-editar-nombre"
            v-model="nombre"
            type="text"
            placeholder="Ej: Mesa 1"
            class="mt-1"
          />
          <p v-if="errorNombreDuplicado" class="mt-1 text-xs text-danger">
            {{ errorNombreDuplicado }}
          </p>
        </div>
        <div v-if="sectoresStore.sectores.length > 0" class="mt-4">
          <FormLabel htmlFor="mesa-editar-sector">Sector</FormLabel>
          <FormSelect id="mesa-editar-sector" v-model="sectorId" class="mt-1">
            <option
              v-for="s in sectoresStore.sectores"
              :key="s.id"
              :value="s.id"
            >
              {{ s.nombre }}
            </option>
          </FormSelect>
        </div>

        <div v-if="estaLibre" class="mt-4">
          <FormSwitch class="flex items-center">
            <FormSwitch.Input
              id="mesa-editar-activa"
              type="checkbox"
              v-model="activa"
            />
            <FormSwitch.Label class="ml-2">Activa</FormSwitch.Label>
          </FormSwitch>
          <p
            v-if="!activa"
            class="mt-2 text-xs text-slate-500 dark:text-slate-400"
          >
            Al desactivar la mesa no estará disponible para utilizar en pedidos,
            tampoco se podrá visualizar en mapa de mesas. Podrás volver a
            activarla cuando desees.
          </p>
        </div>
      </Dialog.Description>

      <Dialog.Footer>
        <div class="flex flex-wrap items-center justify-end gap-2 w-full">
          <Button
            variant="outline-secondary"
            :disabled="cargando"
            @click="() => emit('cerrar')"
          >
            Cerrar
          </Button>
          <Button variant="primary" :disabled="cargando" @click="guardar">
            <LoadingIcon v-if="cargando" icon="oval" class="w-4 h-4 mr-1" />
            Guardar
          </Button>
        </div>
      </Dialog.Footer>
    </Dialog.Panel>
  </Dialog>
</template>
